/**
 * 애플리케이션 로거
 *
 * 책임:
 * - 흩어진 console.* 호출을 단일 입구로 통합
 * - 환경(prod/dev) 또는 외부 SDK(Sentry 등) 도입 시 한 곳만 변경
 * - 테스트 시 vi.mock('@/core/logger')로 손쉽게 silence/검증 가능
 *
 * @why
 * - 프로덕션에서 사용자 환경의 에러를 추적하려면(=관측성) 로깅 호출이
 *   하나의 진입점을 거쳐야 한다. console.* 직접 호출이 흩어져 있으면
 *   나중에 외부 로깅 서비스 연결 시 모든 호출처를 일일이 고쳐야 한다.
 * - 부수 효과로 테스트 출력이 깨끗해진다.
 */

type LogArgs = readonly unknown[]

export interface Logger {
  /** 일반 정보 — 평소엔 무시해도 되지만 디버깅에 유용 */
  info: (...args: LogArgs) => void
  /** 경고 — 비정상이지만 동작은 계속됨 */
  warn: (...args: LogArgs) => void
  /** 에러 — 사용자에게 영향이 있는 실패 */
  error: (...args: LogArgs) => void
  /** 디버그 — 상세 추적용 (production에서는 출력 안 함) */
  debug: (...args: LogArgs) => void
}

const isProduction = (() => {
  try {
    return import.meta.env.MODE === 'production'
  } catch {
    return false
  }
})()

export const logger: Logger = {
  info: (...args) => console.log(...args),
  warn: (...args) => console.warn(...args),
  error: (...args) => console.error(...args),
  debug: (...args) => {
    if (!isProduction) console.debug(...args)
  },
}
